import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import AccountBoxRounded from '@mui/icons-material/AccountBoxRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import WalletRounded from '@mui/icons-material/WalletRounded';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Dialog,
  DialogContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import type { Contact } from '../../../components/pickers';
import { money } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { currencySymbol, type PaymentMethod } from '../lib/api';
import {
  CustomerAccountCard,
  useCreditCheck,
  useCustomerAccount,
} from '../components/CustomerAccount';
import { quickCash, tender } from '../lib/tender';
import { dec, toFixed } from '../lib/decimal';
import { Kbd } from './bits';

const KIND_ICON: Record<PaymentMethod['kind'], ReactNode> = {
  CASH: <PaymentsRounded />,
  CARD: <CreditCardRounded />,
  BANK_TRANSFER: <AccountBalanceRounded />,
  CHEQUE: <ReceiptLongRounded />,
  OTHER: <WalletRounded />,
};

export interface TenderInput {
  methodId: string | null;
  amountReceived: string;
  reference: string | null;
}

/** Payment at the counter: method, amount handed over, change due or the part left on account. */
export function TenderDialog({
  open,
  total,
  customer,
  methods,
  busy,
  error,
  onCancel,
  onComplete,
}: {
  open: boolean;
  total: string;
  customer: Contact | null;
  methods: PaymentMethod[];
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onComplete: (input: TenderInput) => void;
}) {
  const { format } = useShop();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const d = format.currencyDecimals;
  const cash = methods.find((m) => m.kind === 'CASH') ?? methods[0];
  const [methodId, setMethodId] = useState<string | null>(cash?.id ?? null);
  const [amount, setAmount] = useState(toFixed(dec(total), d));
  const [reference, setReference] = useState('');
  const [credit, setCredit] = useState(false);
  const method = methods.find((m) => m.id === methodId) ?? cash;
  const walkIn = !customer || customer.isWalkIn;

  const tendered = credit ? '0' : amount;
  const t = tender(total, tendered, d);
  const short = dec(t.remaining) > 0n;
  const account = useCustomerAccount(walkIn ? null : customer.id, customer);
  const { check, blocked: overLimit } = useCreditCheck(account, t.remaining);
  const blocked = (walkIn && short) || overLimit || busy;

  const submit = () => {
    if (blocked) return;
    onComplete({
      methodId: method?.id ?? null,
      amountReceived: credit ? '0' : amount || '0',
      reference: reference.trim() || null,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={phone}
      slotProps={{ paper: { sx: { overflow: 'hidden' } } }}
    >
      <Box
        sx={(th) => {
          const p = (th.vars ?? th).palette.primary.main;
          return {
            px: 3,
            pt: 3,
            pb: 2.5,
            background: `linear-gradient(135deg, color-mix(in srgb, ${p} 14%, transparent), color-mix(in srgb, ${p} 3%, transparent))`,
            borderBottom: 1,
            borderColor: 'divider',
          };
        }}
      >
        <Typography variant="overline" color="text.secondary">
          Amount due{walkIn ? '' : ` · ${customer.name}`}
        </Typography>
        <Typography
          variant="h1"
          className="tabular"
          sx={{ fontSize: { xs: '2.25rem', sm: '2.75rem' } }}
        >
          {money(total, format)}
        </Typography>
      </Box>
      <DialogContent sx={{ pt: '20px !important' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Payment method
              </Typography>
              <Box
                role="radiogroup"
                aria-label="Payment method"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 1,
                }}
              >
                {methods.map((m) => (
                  <MethodTile
                    key={m.id}
                    selected={m.id === method?.id && !credit}
                    icon={KIND_ICON[m.kind]}
                    label={m.name}
                    onClick={() => {
                      setCredit(false);
                      setMethodId(m.id);
                      if (m.kind !== 'CASH') setAmount(toFixed(dec(total), d));
                    }}
                  />
                ))}
                {!walkIn && (
                  <MethodTile
                    selected={credit}
                    icon={<AccountBoxRounded />}
                    label="On account"
                    onClick={() => setCredit(true)}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {walkIn
                  ? 'Choose a customer to put a sale on account.'
                  : credit
                    ? `Nothing is paid now: the full amount goes on ${customer.name}’s account.`
                    : `Take part now and the rest goes on ${customer.name}’s account.`}
              </Typography>
            </Box>

            {!credit && (
              <Box>
                <TextField
                  label="Amount received"
                  value={amount}
                  autoFocus
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'decimal',
                      className: 'tabular',
                      'aria-label': 'Amount received',
                    },
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">{currencySymbol(format)}</InputAdornment>
                      ),
                      sx: { height: 60, fontSize: '1.5rem', fontWeight: 650 },
                    },
                  }}
                />
                {method?.kind === 'CASH' && (
                  <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                    {quickCash(total, d).map((v, i) => (
                      <Chip
                        key={v}
                        label={i === 0 ? 'Exact' : money(v, { ...format, currencyDecimals: 0 })}
                        onClick={() => setAmount(v)}
                        variant={dec(v) === dec(amount || '0') ? 'filled' : 'outlined'}
                        color={dec(v) === dec(amount || '0') ? 'primary' : 'default'}
                        sx={{ height: 34, fontSize: '0.8125rem', px: 0.5 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {!credit && method && method.kind !== 'CASH' && (
              <TextField
                label="Reference (optional)"
                placeholder={
                  method.kind === 'CARD' ? 'Card slip / approval code' : 'Transfer or cheque number'
                }
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            )}

            <Box
              sx={(th) => {
                const tone = short ? (walkIn ? 'error' : 'warning') : 'success';
                const c = (th.vars ?? th).palette[tone].main;
                return {
                  p: 2,
                  borderRadius: '14px',
                  border: `1px solid color-mix(in srgb, ${c} 28%, transparent)`,
                  bgcolor: `color-mix(in srgb, ${c} 9%, transparent)`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                };
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {short ? (walkIn ? 'Still to pay' : 'On account') : 'Change due'}
                </Typography>
              </Box>
              <Typography
                variant="h2"
                className="tabular"
                sx={{ color: short ? (walkIn ? 'error.main' : 'warning.main') : 'success.main' }}
              >
                {money(short ? t.remaining : t.change, format)}
              </Typography>
            </Box>

            {walkIn && short && (
              <Alert severity="error">
                A walk-in sale must be paid in full. Choose a customer to put the rest on account.
              </Alert>
            )}
            {!walkIn && check && (
              <CustomerAccountCard
                name={customer.name}
                check={check}
                adding={t.remaining}
                blocked={overLimit}
                dense
              />
            )}
            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" spacing={1.25}>
              <Button
                variant="outlined"
                size="large"
                onClick={onCancel}
                disabled={busy}
                sx={{ flex: 1 }}
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                loading={busy}
                disabled={blocked}
                sx={{ flex: 2, minHeight: 52, fontSize: '1rem' }}
                endIcon={<Kbd inverse>Enter</Kbd>}
              >
                {credit ? 'Complete on account' : 'Complete sale'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MethodTile({
  selected,
  icon,
  label,
  onClick,
}: {
  selected: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      role="radio"
      aria-checked={selected}
      onClick={onClick}
      sx={(th) => {
        const p = (th.vars ?? th).palette.primary.main;
        return {
          flexDirection: 'column',
          gap: 0.5,
          py: 1.5,
          minHeight: 72,
          borderRadius: '12px',
          border: 1.5,
          borderStyle: 'solid',
          borderColor: selected ? p : 'divider',
          bgcolor: selected ? `color-mix(in srgb, ${p} 8%, transparent)` : 'background.paper',
          color: selected ? 'primary.main' : 'text.primary',
          fontWeight: 600,
          transition: 'all 140ms',
          '&:hover': { borderColor: selected ? p : 'grey.300' },
        };
      }}
    >
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
    </ButtonBase>
  );
}
